import React from 'react';
import { motion } from 'framer-motion';
import {
    FileCode, Palette, Code2, Atom, Layers,
    Wind, Server, Braces, Lock, Database,
    GitBranch, Box, Zap, Cloud, Cpu,
    FileJson, Workflow, CreditCard, Terminal, Infinity
} from 'lucide-react';

const technologies = [
    {
        name: "HTML5",
        description: "Semantic, accessible & SEO optimized markup.",
        icon: <FileCode className="w-6 h-6" />
    },
    {
        name: "CSS3",
        description: "Modern layouts using Flexbox & Grid system.",
        icon: <Palette className="w-6 h-6" />
    },
    {
        name: "JavaScript (ES6+)",
        description: "Dynamic UI logic and modern scripting.",
        icon: <Code2 className="w-6 h-6" />
    },
    {
        name: "React.js",
        description: "Component-based scalable frontend architecture.",
        icon: <Atom className="w-6 h-6" />
    },
    {
        name: "Next.js",
        description: "SSR, SSG & performance optimized framework.",
        icon: <Layers className="w-6 h-6" />
    },
    {
        name: "Tailwind CSS",
        description: "Utility-first rapid UI styling system.",
        icon: <Wind className="w-6 h-6" />
    },
    {
        name: "Node.js",
        description: "High-performance backend runtime.",
        icon: <Server className="w-6 h-6" />
    },
    {
        name: "REST APIs",
        description: "Secure and scalable API integrations.",
        icon: <Braces className="w-6 h-6" />
    },
    {
        name: "Authentication",
        description: "JWT & secure user authentication systems.",
        icon: <Lock className="w-6 h-6" />
    },
    {
        name: "MongoDB / SQL",
        description: "Reliable and scalable database solutions.",
        icon: <Database className="w-6 h-6" />
    },
    {
        name: "Git & GitHub",
        description: "Version control and team collaboration.",
        icon: <GitBranch className="w-6 h-6" />
    },
    {
        name: "NPM / Yarn",
        description: "Efficient package and dependency management.",
        icon: <Box className="w-6 h-6" />
    },
    {
        name: "Performance Optimization",
        description: "Image optimization & lazy loading.",
        icon: <Zap className="w-6 h-6" />
    },
    {
        name: "Vercel",
        description: "Fast global deployment infrastructure.",
        icon: <Cloud className="w-6 h-6" />
    },
    {
        name: "Modern Tooling",
        description: "Webpack, Turbopack & build optimization.",
        icon: <Cpu className="w-6 h-6" />
    },
    {
        name: "TypeScript",
        description: "Strongly typed JavaScript for scale.",
        icon: <FileJson className="w-6 h-6" />
    },
    {
        name: "State Management",
        description: "Context API, Redux & Zustand.",
        icon: <Workflow className="w-6 h-6" />
    },
    {
        name: "Payments",
        description: "Secure Stripe & Razorpay integration.",
        icon: <CreditCard className="w-6 h-6" />
    },
    {
        name: "Testing",
        description: "Jest & RTL for reliable applications.",
        icon: <Terminal className="w-6 h-6" />
    },
    {
        name: "CI/CD",
        description: "Automated deployment workflows.",
        icon: <Infinity className="w-6 h-6" />
    }
];

const TechStack = () => {
    return (
        <section className="py-24 md:py-48 min-h-[auto] md:min-h-screen flex items-center bg-white relative overflow-hidden z-10">
            {/* Background Decor */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                <div className="absolute top-1/4 -left-1/4 w-1/3 h-1/3 bg-blue-500/5 rounded-full blur-[100px]" />
                <div className="absolute bottom-1/4 -right-1/4 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-[100px]" />
            </div>

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 md:mb-16 gap-6">
                    <div className="max-w-3xl">
                        <motion.span
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-primary font-black uppercase tracking-[0.4em] text-[10px] md:text-xs mb-3 block"
                        >
                            Technical Excellence
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="text-2xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-[1.1] uppercase tracking-tight"
                        >
                            WE USE <span className="text-primary">POWERFUL</span> <br className="hidden md:block" />
                            <span className="text-primary">TECHNOLOGIES.</span>
                        </motion.h2>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="md:max-w-sm text-slate-500 text-xs md:text-lg border-l-2 border-primary/30 pl-6 py-1.5 font-medium"
                    >
                        Modern, scalable and high-performance tech stack for future-ready platforms.
                    </motion.div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 border-t border-l border-slate-100 bg-slate-50/[0.3]">
                    {technologies.map((tech, index) => (
                        <motion.div
                            key={tech.name}
                            initial={{ opacity: 0, y: 15 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.02 }}
                            className="p-6 md:p-8 border-r border-b border-slate-100 hover:bg-white transition-all group relative overflow-hidden"
                        >
                            <div className="absolute -right-4 -bottom-4 w-20 h-20 bg-blue-500/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-all"></div>
                            <div className="relative z-10">
                                <div className="mb-4 md:mb-6 text-slate-300 group-hover:text-primary transition-all group-hover:scale-110 group-hover:rotate-3 origin-left">
                                    {React.cloneElement(tech.icon, { className: "w-6 h-6 md:w-10 md:h-10" })}
                                </div>
                                <h3 className="text-sm md:text-lg font-black text-slate-900 mb-2 tracking-tight group-hover:text-primary transition-colors">
                                    {tech.name}
                                </h3>
                                <p className="text-[10px] md:text-sm text-slate-500 leading-relaxed font-semibold group-hover:text-slate-700 transition-colors">
                                    {tech.description}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default TechStack;
